import {
	DateString,
	FormattedText,
	Highlight,
	HighlightedText,
	Icon,
	ListItem,
	Space,
	Typography
} from "client/Components";
import {
	EMPTY_ARRAY,
	generateTrackingId,
	isNotNil
} from "client/Misc/utils";
import {
	ReactElement,
	useCallback
} from "react";
import { Thumbnail } from "./Thumbnail";
import { localize } from "client_legacy/ndaw/culture";
import { SubTitleSection } from "./SubTitleSection";
import { useHighlights } from "../ResultView/CategoryView/ItemView/useHighlights";
import { HighlightItem } from "client/Api/TitleBar/GlobalSearch/globalSearch";
import { FileThumbnailKind, getDocumentKind } from "client_legacy/Documents/fileExtensions";
import { DocumentItemType } from "client/Api/Documents/SharePoint/common";
import { navigateTo } from "client_legacy/ndaw/navigator";
import { TimeZone } from "client/DateAndTime/timezones";
import { DocumentLocation } from "./DocumentLocation";
import { useGlobalSearchItemActions } from "../GlobalSearchItem/useGlobalSearchItemActions";

export type EgnyteListItemProps = {
	isMobile: boolean;
	hitHighlights?: readonly HighlightItem[];
	extension: string;
	name: string;
	path: string;
	url: string;
	lastModifiedDate: string | Date;
	modifiedBy: string;
	locationUrl: string;
	onDismiss?: () => void;
	onRemove?: () => void;
	timezone: TimeZone;
	itemIndex: number;
	tabIndex: number;
	onExecute: () => void;
};

export const EgnyteListItem = (props: EgnyteListItemProps): ReactElement => {
	const {
		isMobile,
		hitHighlights = EMPTY_ARRAY,
		extension,
		lastModifiedDate,
		locationUrl,
		modifiedBy,
		name,
		path,
		url,
		onDismiss,
		onRemove,
		timezone,
		itemIndex,
		tabIndex,
		onExecute
	} = props;

	const highlights: Map<string, Highlight> = useHighlights(hitHighlights);

	const type: FileThumbnailKind = getDocumentKind(
		DocumentItemType.File,
		extension
	);

	const locationClickHandler = useCallback(
		(): void => {
			navigateTo(locationUrl, true);
			onDismiss?.();
		},
		[locationUrl, onDismiss]
	);

	const {
		onKeyDown,
		ref
	} = useGlobalSearchItemActions({
		itemIndex,
		onExecute
	});

	return (
		<ListItem
			trackingId={generateTrackingId("egnyte-result-list-item", `|${url}`)}
			noMobileBorderBottom
			tabIndex={tabIndex}
			ref={ref}
			onKeyDown={onKeyDown}
		>
			<ListItem.Content
				thumbnail={
					<Thumbnail trackingId="thumbnail-container">
						<Icon
							value={{ kind: "svg", name: type }}
						/>
					</Thumbnail>
				}
			>
				<ListItem.Title>
					<Typography
						weight="bold"
						trackingId="title"
					>
						<HighlightedText
							text={name}
							highlight={highlights.get("name")}
						/>
					</Typography>
				</ListItem.Title>
				<ListItem.SubTitle>
					<SubTitleSection
						trackingId="modified-date"
						text={(
							<>
								<ListItem.SubTitleIcon kind="pen" />
								<Space />
								<DateString
									format="RelativeTimestamp"
									value={lastModifiedDate}
									timezone={timezone}
								/>
							</>
						)}
					/>

					<SubTitleSection
						trackingId="created-by"
						text={(
							<FormattedText
								text={localize("_CreatedByName")}
								thingsToReplace={[
									{ what: "{{Name}}", filler: modifiedBy }
								]}
							/>
						)}
					/>

					<SubTitleSection
						trackingId="location"
						text={(
							<DocumentLocation
								onClick={locationClickHandler}
							>
								{formatBreadCrumb(path)}
							</DocumentLocation>
						)}
					/>
				</ListItem.SubTitle>
			</ListItem.Content>
			{isMobile && (
				<ListItem.Action
					icon="folder-open"
					onClick={locationClickHandler}
					bubbleClick={false}
					trackingId="open-location"
				/>
			)}
			{isNotNil(onRemove) && (
				<ListItem.Action
					icon="xmark"
					onClick={() => onRemove()}
					bubbleClick={false}
					trackingId="remove"
				/>
			)}
		</ListItem>
	);
};
EgnyteListItem.displayName = "EgnyteListItem";

export function formatBreadCrumb(path: string): string {
	const segments: string[] = path.replace("\\", "/")
		.split("/");

	if (segments[0]?.trim() === "") {
		segments.shift();
	}

	// remove the file item itself
	segments.pop();

	if (segments.length > 3) {
		return segments[0] + " » ... » " + segments[segments.length - 1];
	}

	return segments.join(" » ");
}
